/**
 * 🎙 **엄마·아빠가 읽어 준 그대로** — 보호자가 쪽마다 녹음한 소리를 **이 기기 안에만** 둔다
 * (2026-11-01 · 리서치 최종안 ⑦ · docs/build/37 §6).
 *
 * ## 🔴 서버로 보내지 않는다(불변식 1)
 * 목소리는 생체 정보다. 서버에 두면 「누구의 목소리인지」를 우리가 들고 있게 된다 — 이 서비스는
 * 그런 것을 안 만든다. 그래서 이 파일에는 **fetch 가 없다**(`familyVoice.test` 가 지킨다).
 * 다른 기기로 옮기는 길도 없다 — 옮기려면 서버를 거쳐야 하므로 일부러 안 만든다.
 *
 * ## 왜 IndexedDB 인가
 * 녹음은 쪽마다 수십 KB~1MB 라 localStorage(문자열·5MB)에 못 담는다. IndexedDB 는 Blob 을 그대로 담는다.
 * 🔴 iOS 사파리는 7일 안 열면 기기 저장소를 지운다 — 홈 화면 앱은 예외(보호자 화면의 「홈 화면에 추가」 안내).
 *
 * ## 🔴 지우는 길이 둘 다 있어야 한다
 * 쪽마다 지우기(녹음 화면)와 [처음부터 다시 하기](`resetLocal`) — 후자에서 빠지면 **다시는 못 지우는 녹음**이 남는다.
 */

/** DB 이름 — `gc_` 로 시작해 이 서비스 것임을 안다. [처음부터 다시 하기]가 이 이름으로 지운다. */
export const VOICE_DB = "gc_family_voice";
const STORE = "clips";

/** 한 쪽 녹음. */
export interface VoiceClip {
  bookId: string;
  pageId: string;
  blob: Blob;
  /** 녹음 길이(밀리초) — 목록에 「12초」로 보인다. */
  ms: number;
  /** 녹음한 때(기기 시각). */
  at: number;
}

const keyOf = (bookId: string, pageId: string) => `${bookId}/${pageId}`;

/** 이 브라우저가 기기 저장(IndexedDB)을 쓸 수 있나 — 못 쓰면 녹음 화면이 그렇게 말한다. */
export function canKeepVoice(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(VOICE_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("indexedDB open"));
    // 다른 탭이 옛 판을 쥐고 있으면 멈춘다 — 「못 열었다」로 끝낸다(영원히 기다리지 않게)
    req.onblocked = () => reject(new Error("indexedDB blocked"));
  });
}

/** 한 번 열고, 할 일을 하고, 닫는다. 실패는 그대로 던진다 — 부르는 화면이 「못 했다」를 말한다. */
async function withStore<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  const db = await openDb();
  try {
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = run(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(req ? req.result : undefined);
      tx.onerror = () => reject(tx.error ?? new Error("indexedDB tx"));
      tx.onabort = () => reject(tx.error ?? new Error("indexedDB abort"));
    });
  } finally {
    db.close();
  }
}

/** 남긴다(같은 쪽이면 덮어쓴다 — 다시 녹음). 못 남기면 던진다(자리가 모자람 등). */
export async function saveClip(bookId: string, pageId: string, blob: Blob, ms: number): Promise<void> {
  const clip: VoiceClip = { bookId, pageId, blob, ms: Math.max(0, Math.round(ms)), at: Date.now() };
  await withStore("readwrite", (s) => s.put(clip, keyOf(bookId, pageId)));
  await markCount();
}

/** 한 쪽을 지운다. */
export async function deleteClip(bookId: string, pageId: string): Promise<void> {
  await withStore("readwrite", (s) => s.delete(keyOf(bookId, pageId)));
  await markCount();
}

/**
 * 🔴 **녹음이 있다는 표시를 바로 읽을 수 있는 곳에도** — [처음부터 다시 하기]의 확인 창은 동기라
 * IndexedDB 를 기다릴 수 없다. 표시가 없으면 「목소리도 지워져요」를 못 말하고 지운다(되돌릴 수 없다).
 * 키가 gc_ 로 시작해 초기화 때 같이 지워진다.
 */
const COUNT_KEY = "gc_family_voice_n";
async function markCount(): Promise<void> {
  const n = (await allClips()).length;
  try {
    localStorage.setItem(COUNT_KEY, String(n));
  } catch (err) {
    // 표시만 못 남겼다 — 녹음은 남았다. 초기화 확인 창이 「목소리」 줄을 빠뜨릴 수 있어 남겨 둔다
    console.warn("녹음 수 표시를 못 남겼어요", err);
  }
}

/** 이 기기에 보호자 녹음이 있나(동기 · 초기화 확인 창용). 못 읽으면 「있다」로 — 지우기 전에는 더 말하는 쪽이 안전하다. */
export function familyVoiceRecorded(): boolean {
  try {
    return Number(localStorage.getItem(COUNT_KEY) ?? "0") > 0;
  } catch {
    return true; // 못 읽었다 — 지우기 전이라 「있다」고 말하는 쪽이 안전하다(위 설명)
  }
}

/** 이 기기의 녹음 전부(책 가리지 않고). 책이 몇 권 안 돼 전부 읽어도 가볍다. */
export async function allClips(): Promise<VoiceClip[]> {
  return ((await withStore<VoiceClip[]>("readonly", (s) => s.getAll() as IDBRequest<VoiceClip[]>)) ?? []).filter(isClip);
}

/** 한 권의 녹음. */
export async function clipsOf(bookId: string): Promise<VoiceClip[]> {
  return (await allClips()).filter((c) => c.bookId === bookId);
}

/** 🔴 기기에 있던 값도 밖에서 온 값이다 — 모양이 아니면 버린다(불변식 7). */
function isClip(v: unknown): v is VoiceClip {
  const c = v as VoiceClip | null;
  return !!c && typeof c.bookId === "string" && typeof c.pageId === "string" && c.blob instanceof Blob && typeof c.ms === "number";
}

/** 책마다 몇 쪽 녹음했나 — 보호자 화면 목록. */
export function countByBook(clips: readonly VoiceClip[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of clips) out[c.bookId] = (out[c.bookId] ?? 0) + 1;
  return out;
}

/**
 * 🗑 **전부 지운다** — [처음부터 다시 하기].
 * 이 DB 를 쥔 탭이 있으면 그 탭이 닫힐 때 지워진다(onblocked) — 그건 실패가 아니라 「곧」이다.
 */
export function wipeFamilyVoice(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!canKeepVoice()) return resolve();
    const req = indexedDB.deleteDatabase(VOICE_DB);
    req.onsuccess = () => resolve();
    req.onblocked = () => resolve();
    req.onerror = () => reject(req.error ?? new Error("indexedDB delete"));
  });
}

// ── 읽어 줄 때 쓸지 ─────────────────────────────────────────

const ON_KEY = "gc_family_voice_on";

/**
 * 책을 읽어 줄 때 녹음을 쓸지. **기본은 쓴다** — 녹음한 사람은 그 소리를 들려주려고 녹음했다.
 * 보호자가 끄면 원래 읽어주기로 돌아간다(녹음은 그대로 남는다).
 */
export function familyVoiceOn(): boolean {
  try {
    return localStorage.getItem(ON_KEY) !== "0";
  } catch {
    return true; // 저장소를 못 읽는 기기 — 기본값(쓴다). 못 읽은 것으로 녹음을 막을 이유가 없다
  }
}

/** 고르기. 못 남겼으면 `false` — 화면이 「이 기기에 기억하지 못했어요」를 말한다. */
export function setFamilyVoiceOn(on: boolean): boolean {
  try {
    localStorage.setItem(ON_KEY, on ? "1" : "0");
    return true;
  } catch {
    return false;
  }
}

// ── 녹음 형식 ─────────────────────────────────────────────

/**
 * 이 브라우저가 녹음할 형식. 크롬·안드로이드는 webm(opus), 사파리는 mp4(aac).
 * 없으면 브라우저 기본값에 맡긴다(`""`).
 */
export function recordMime(): string {
  if (typeof MediaRecorder === "undefined") return "";
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"]) {
    if (MediaRecorder.isTypeSupported?.(m)) return m;
  }
  return "";
}

/**
 * 🔴 **blob: 주소에는 확장자가 없다** — 읽어주기 재생기(Howler)는 확장자로 형식을 고르는데,
 * 없으면 「지원하지 않는 형식」으로 못 틀고 **그 쪽을 건너뛴다**(소리 없이). 그래서 주소를 만들 때
 * 형식을 같이 적어 두고 재생기가 묻는다(`useBookAudio`).
 */
export const blobFormat = new Map<string, string>();

export function formatOfMime(mime: string): string {
  if (mime.includes("mp4") || mime.includes("aac") || mime.includes("m4a")) return "mp4";
  if (mime.includes("ogg")) return "ogg";
  return "webm";
}

/** 재생할 주소를 만든다(다 쓰면 `dropClipUrl`). */
export function clipUrl(clip: VoiceClip): string {
  const url = URL.createObjectURL(clip.blob);
  blobFormat.set(url, formatOfMime(clip.blob.type));
  return url;
}

export function dropClipUrl(url: string): void {
  URL.revokeObjectURL(url);
  blobFormat.delete(url);
}
